import { FormikValues } from 'formik'
import { isEmpty } from 'lodash'
import moment from 'moment'
import { IntlShape } from 'react-intl'

import { RepeatOptions } from '@/pages/SpaceAppBuilder/utilities/interfaces'

import messages from './messages'

export const getRepeatOptions = (intl: IntlShape) => [
  { label: intl.formatMessage(messages.minutes), value: RepeatOptions.Minutes },
  { label: intl.formatMessage(messages.hourly), value: RepeatOptions.Hourly },
  { label: intl.formatMessage(messages.daily), value: RepeatOptions.Daily },
  { label: intl.formatMessage(messages.weekly), value: RepeatOptions.Weekly },
  { label: intl.formatMessage(messages.monthly), value: RepeatOptions.Monthly },
  { label: intl.formatMessage(messages.yearly), value: RepeatOptions.Yearly }
]

export const getDaysOfWeek = (intl: IntlShape) => [
  { key: intl.formatMessage(messages.monday), value: 'MON', text: intl.formatMessage(messages.monday) },
  { key: intl.formatMessage(messages.tuesday), value: 'TUE', text: intl.formatMessage(messages.tuesday) },
  { key: intl.formatMessage(messages.wednesday), value: 'WED', text: intl.formatMessage(messages.wednesday) },
  { key: intl.formatMessage(messages.thursday), value: 'THU', text: intl.formatMessage(messages.thursday) },
  { key: intl.formatMessage(messages.friday), value: 'FRI', text: intl.formatMessage(messages.friday) },
  { key: intl.formatMessage(messages.saturday), value: 'SAT', text: intl.formatMessage(messages.saturday) },
  { key: intl.formatMessage(messages.sunday), value: 'SUN', text: intl.formatMessage(messages.sunday) }
]

export const getDaysOfWeekNumeric = (intl: IntlShape) => [
  { label: intl.formatMessage(messages.sunday), value: 1 },
  { label: intl.formatMessage(messages.monday), value: 2 },
  { label: intl.formatMessage(messages.tuesday), value: 3 },
  { label: intl.formatMessage(messages.wednesday), value: 4 },
  { label: intl.formatMessage(messages.thursday), value: 5 },
  { label: intl.formatMessage(messages.friday), value: 6 },
  { label: intl.formatMessage(messages.saturday), value: 7 }
]

export const getMonthsOfYear = (intl: IntlShape) => [
  { label: intl.formatMessage(messages.january), value: 'JAN' },
  { label: intl.formatMessage(messages.february), value: 'FEB' },
  { label: intl.formatMessage(messages.march), value: 'MAR' },
  { label: intl.formatMessage(messages.april), value: 'APR' },
  { label: intl.formatMessage(messages.may), value: 'MAY' },
  { label: intl.formatMessage(messages.june), value: 'JUN' },
  { label: intl.formatMessage(messages.july), value: 'JUL' },
  { label: intl.formatMessage(messages.august), value: 'AUG' },
  { label: intl.formatMessage(messages.september), value: 'SEP' },
  { label: intl.formatMessage(messages.october), value: 'OCT' },
  { label: intl.formatMessage(messages.november), value: 'NOV' },
  { label: intl.formatMessage(messages.december), value: 'DEC' }
]

export const getWeekOfMonth = (intl: IntlShape) => [
  { label: intl.formatMessage(messages.first), value: 1 },
  { label: intl.formatMessage(messages.second), value: 2 },
  { label: intl.formatMessage(messages.third), value: 3 },
  { label: intl.formatMessage(messages.fourth), value: 4 },
  { label: intl.formatMessage(messages.fifth), value: 5 }
]

export const getMinutes = (isUserSchedule: boolean = false) => {
  return [5, 10, 15, 20, 25, 30, 45]
    .filter((minuteInterval: number) => (isUserSchedule ? ![5, 10].includes(minuteInterval) : true))
    .map(num => ({ label: num, value: num }))
}

export const getNumericRangeOptions = (number: number) => {
  return Array.from({ length: number }, (v, k) => ({ label: k + 1, value: k + 1 }))
}

export const getScheduleType = (intl: IntlShape) => [
  {
    text: intl.formatMessage(messages.forUsers),
    description: intl.formatMessage(messages.forUsersDescription),
    value: true
  },
  {
    text: intl.formatMessage(messages.scheduledJob),
    description: intl.formatMessage(messages.scheduledJobDescription),
    value: false
  }
]

/**
 * Will transform api payload to UI field structure
 * convert string to dates so date picker can be used for time and day/month selection
 * @param values
 */
export function getTransformedUIFields(values: FormikValues) {
  const { triggerData } = values
  const time = new Date(`${new Date().toISOString().slice(0, 10)} ${triggerData?.time || '00:00'}`)
  const dayMonth = triggerData?.dayMonth && new Date(`${triggerData.dayMonth} ${new Date().getFullYear()}`)
  let repeats = triggerData?.repeats || RepeatOptions.OneTime
  const isVerbose = repeats.includes('Verbose').toString()
  if (repeats.includes('monthly')) {
    repeats = 'monthly'
  }
  if (repeats.includes('yearly')) {
    repeats = 'yearly'
  }
  return { time, dayMonth, repeats, isVerbose }
}

export const padDateLeft = (date: Date) => {
  return date.toString().padStart(2, '0')
}

export const getScheduleText = (values: FormikValues, intl: IntlShape, isTriggerNode: boolean) => {
  const { time, dayMonth, repeats, isVerbose } = isTriggerNode ? getTransformedUIFields(values) : values.triggerData
  const {
    days,
    months,
    minutes,
    hours,
    date,
    dayOfWeek,
    weekOfMonth,
    day,
    monthOfYear,
    isUserSchedule,
    audience,
    timeZone
  } = values.triggerData

  let text = intl.formatMessage(messages.schedulePrefixText)
  text =
    repeats === RepeatOptions.OneTime
      ? text.concat(` ${intl.formatMessage(messages.run)}`)
      : text.concat(` ${intl.formatMessage(messages.repeat)}`)

  let scheduleText
  switch (repeats) {
    case RepeatOptions.OneTime: {
      scheduleText = !!date && ` ${intl.formatMessage(messages.on)} **${moment(date).format('lll')}**`
      break
    }
    case RepeatOptions.Minutes: {
      scheduleText =
        !!minutes &&
        `  **${intl.formatMessage(messages.everyMinute, {
          time: minutes
        })}**`
      break
    }
    case RepeatOptions.Hourly: {
      scheduleText =
        !!hours &&
        `  **${intl.formatMessage(messages.everyHour, {
          time: hours
        })}**`
      break
    }
    case RepeatOptions.Daily: {
      scheduleText =
        !!time &&
        `  **${intl.formatMessage(messages.everyDay)}** ${intl.formatMessage(messages.at)} **${moment(time).format(
          'h:mma'
        )}**`
      break
    }
    case RepeatOptions.Weekly: {
      scheduleText =
        !!days &&
        !!time &&
        `  **${intl.formatMessage(messages.every)} ${days
          .map((day: string) => getDaysOfWeek(intl).find(dow => dow.value === day)?.text)
          .join(', ')
          .replace(/, ((?:.(?!, ))+)$/, ' and $1')}** ${intl.formatMessage(messages.at)} **${moment(time).format(
          'h:mma'
        )}**`
      break
    }
    case RepeatOptions.Monthly: {
      if (isVerbose === 'true') {
        scheduleText =
          !!weekOfMonth && !!dayOfWeek && !!months && !!time
            ? ` ${intl.formatMessage(messages.onThe)} **${
                getWeekOfMonth(intl)?.find(wom => wom.value === Number.parseInt(weekOfMonth))?.label
              } ${
                getDaysOfWeekNumeric(intl).find(dow => dow.value === Number.parseInt(dayOfWeek))?.label
              }** ${intl.formatMessage(messages.of)} **${intl.formatMessage(
                messages.every
              )} ${months} ${intl.formatMessage(messages.months)}** ${intl.formatMessage(messages.at)} **${moment(
                time
              ).format('h:mma')}**`
            : ''
      } else {
        scheduleText =
          !!months && !!day && !!time
            ? ` ${intl.formatMessage(messages.every)} **${months} ${intl.formatMessage(
                messages.months
              )}** ${intl.formatMessage(messages.onDay)} **${day}** ${intl.formatMessage(messages.at)} **${moment(
                time
              ).format('h:mma')}**`
            : ''
      }
      break
    }
    case RepeatOptions.Yearly: {
      if (isVerbose === 'true') {
        scheduleText =
          !!weekOfMonth && !!dayOfWeek && !!monthOfYear && !!time
            ? `  **${intl.formatMessage(messages.every)} ${intl.formatMessage(messages.year)}** ${intl.formatMessage(
                messages.onThe
              )}  **${getWeekOfMonth(intl)?.find(wom => wom.value === Number.parseInt(weekOfMonth))?.label} ${
                getDaysOfWeekNumeric(intl).find(dow => dow.value === Number.parseInt(dayOfWeek))?.label
              }** ${intl.formatMessage(messages.in)} **${
                getMonthsOfYear(intl)?.find(wom => wom.value === monthOfYear)?.label
              }** ${intl.formatMessage(messages.at)} **${moment(time).format('h:mma')}**`
            : ''
      } else {
        scheduleText =
          !!time && !!dayMonth
            ? `  **${intl.formatMessage(messages.every)} ${intl.formatMessage(messages.year)}** ${intl.formatMessage(
                messages.onThe
              )} ** ${moment(dayMonth).format('Do MMMM')} ** ${intl.formatMessage(messages.at)} **${moment(time).format(
                'h:mma'
              )}**`
            : ''
      }
      break
    }
  }

  const prettyTimeZone = timeZone ? timeZone?.replaceAll('_', ' ') : 'UTC'
  if (isEmpty(scheduleText)) {
    return ''
  } else {
    text = text.concat(scheduleText || '')
    text = isUserSchedule && !isEmpty(audience) ? text.concat(` ${intl.formatMessage(messages.forAudience)}`) : text
    text = isUserSchedule
      ? text
      : text.concat(
          ` **${intl.formatMessage(messages.schedulePostfixText)} ${prettyTimeZone} ${intl.formatMessage(
            messages.scheduleTimeText
          )}.**`
        )
  }

  return text
}
